import { useNavigate } from 'react-router-dom';
import { useApp, useFetch } from '../store/app';
import { Fade } from './common';
import { Icon } from '../components/Icon';
import { Card, ProgressBar, StatusBadge, Avatar } from '../components/primitives';
import { st } from '../lib/status';
import type { ActivityEntry, Integration } from '../../shared/types';

interface DashStat { label: string; value: string; delta: string; icon: string; dColor: string; dIcon: string; to: string }
interface DashProject { id: number; title: string; client: string; pct: number; status: string }
interface DashData { stats: DashStat[]; projects: DashProject[]; activity: ActivityEntry[]; integrations: Integration[] }

export function Dashboard() {
  const nav = useNavigate();
  const { openDrawer } = useApp();
  const { data } = useFetch<DashData>('/dashboard');
  if (!data) return null;

  return (
    <Fade>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14, marginBottom: 18 }}>
        {data.stats.map((s) => (
          <Card key={s.label} hover onClick={() => nav(s.to)} style={{ padding: 16, borderRadius: 11 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 500 }}>{s.label}</span>
              <Icon name={s.icon} size={19} color="var(--text-3)" />
            </div>
            <div style={{ fontSize: 27, fontWeight: 700, letterSpacing: '-.02em', lineHeight: 1 }}>{s.value}</div>
            <div style={{ marginTop: 8, fontSize: 12, color: s.dColor, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 3 }}>
              <Icon name={s.dIcon} size={15} color={s.dColor} />{s.delta}
            </div>
          </Card>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 14 }}>
        <Card style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ fontWeight: 600, fontSize: 14.5 }}>Active projects</div>
            <div style={{ flex: 1 }} />
            <button onClick={() => nav('/projects')} style={{ appearance: 'none', border: 0, background: 'transparent', color: 'var(--accent)', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 2 }}>
              Open board <Icon name="chevron_right" size={17} />
            </button>
          </div>
          {data.projects.map((p) => (
            <div key={p.id} onClick={() => openDrawer({ type: 'project', mode: 'edit', id: p.id })} className="cc-hover-s2 cc-clickable" style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', alignItems: 'center', gap: 14, padding: '13px 18px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{p.client}</div>
              </div>
              <ProgressBar pct={p.pct} width={130} />
              <StatusBadge badge={st(p.status)} />
            </div>
          ))}
        </Card>

        <Card style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', fontWeight: 600, fontSize: 14.5 }}>Recent activity</div>
          <div style={{ padding: '6px 0' }}>
            {data.activity.map((a) => {
              const tone = st(a.tone);
              return (
                <div key={a.id} style={{ display: 'flex', gap: 11, padding: '10px 18px' }}>
                  <div style={{ width: 30, height: 30, flex: '0 0 auto', borderRadius: '50%', background: tone.bg, color: tone.fg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name={a.icon} size={17} color={tone.fg} />
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.8, lineHeight: 1.4 }}><strong style={{ fontWeight: 600 }}>{a.actor}</strong> {a.action}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 1 }}>{a.created_at}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>

      <Card style={{ marginTop: 14, padding: '16px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <div style={{ fontWeight: 600, fontSize: 14.5 }}>Connected services</div>
          <div style={{ flex: 1 }} />
          <button onClick={() => nav('/onboarding')} style={{ appearance: 'none', border: 0, background: 'transparent', color: 'var(--accent)', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Manage</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
          {data.integrations.map((g) => {
            const badge = st(g.status);
            return (
              <div key={g.id} onClick={() => nav('/settings')} className="cc-hover-border cc-clickable" style={{ border: '1px solid var(--border)', borderRadius: 10, padding: 13, display: 'flex', flexDirection: 'column', gap: 9 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div className="cc-mono" style={{ width: 30, height: 30, borderRadius: 8, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: 'var(--text-2)' }}>{g.mark}</div>
                  <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, background: badge.bg, color: badge.fg, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: badge.fg }} />{badge.label}
                  </span>
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{g.name}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--text-3)', marginTop: 2 }}>{g.last_sync}</div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </Fade>
  );
}
